import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getGoogleMapsLoader } from 'src/app/_helpers/google-maps-loader-helper';
import {
    AlertEventState,
    AlertEventTransitionSource,
    AlertSeverityLevel,
    alertSeverityLabel,
} from 'src/app/enums/alert.enum';
import {
    CapturePublicAlertResponsePayload,
    PublicAlertEventTransitionResult,
    PublicAlertResponseControlOption,
    PublicAlertResponseNode,
    PublicAlertResponsePageData,
} from 'src/app/models/public-alert-response.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastService } from 'src/app/services/toast.service';
import {
    ACK_LINK_CHANNEL_PARAM,
    ACK_LINK_SMS_CHANNEL,
    ALERT_RESPONSE_COLLAPSED_CONTROL_COUNT,
    ALERT_RESPONSE_MAP_ID,
    ALERT_RESPONSE_MAP_PADDING,
    ALERT_RESPONSE_MAP_REFIT_DELAY_MS,
    ALERT_RESPONSE_MAP_ZOOM,
    ALERT_RESPONSE_MAX_NEIGHBOUR_COUNT,
    EARTH_RADIUS_METERS,
} from 'src/app/shared/constants/alertResponse.constants';
import { trackById } from 'src/app/shared/utils/track-by.util';

declare var google: any;

type PageState = 'loading' | 'error' | 'revoked' | 'ready';
type CoverageSurface = 'map' | 'list';
type MapState = 'idle' | 'loading' | 'ready' | 'failed';

interface ControlGroup {
    id: string;
    title: string;
    hint: string;
    options: PublicAlertResponseControlOption[];
}

interface ReadingRow {
    id: string;
    nodeName: string;
    severityClass: string;
    severityLabel: string;
    readingValue: number | null;
    unitLabel: string;
    thresholdValue: number | null;
    detailLabel: string;
    bandTextClass: string;
    startedAt: Date | string | null;
    lastReportedAt: Date | string | null;
    isReporting: boolean;
}

interface TransitionRow {
    id: string;
    nodeName: string;
    fromLabel: string;
    toLabel: string;
}

interface CoverageNodeRow {
    id: string;
    name: string;
    distanceLabel: string;
    isCovered: boolean;
    isLocked: boolean;
}

@Component({
    selector: 'app-alert-response',
    templateUrl: './alert-response.component.html',
    styleUrls: ['./alert-response.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AlertResponseComponent implements OnInit, OnDestroy {
    readonly trackById = trackById;
    readonly timeFormat: string;
    readonly dateTimeFormat: string;

    @ViewChild('mapFrame') mapFrameRef?: ElementRef<HTMLElement>;
    @ViewChild('locateAnchor') locateAnchorRef?: ElementRef<HTMLElement>;
    @ViewChild('coverageAnchor') coverageAnchorRef?: ElementRef<HTMLElement>;

    pageState: PageState = 'loading';
    loadErrorTitle = '';
    loadErrorMessage = '';
    canRetryLoad = false;

    data: PublicAlertResponsePageData | null = null;

    headerTitle = '';
    headerSeverityClass = '';
    headerSeverityLabel = '';
    breachSummaryLabel = '';
    locateSummaryLabel = '';

    readingRows: ReadingRow[] = [];
    transitionRows: TransitionRow[] = [];

    controlGroups: ControlGroup[] = [];
    controlSearchText = '';
    showAllControls = false;
    hiddenControlCount = 0;
    controlCount = 0;
    hasControlMatches = true;
    selectedControlIds: Record<string, boolean> = {};
    selectedControlCount = 0;

    lockedNodeRows: CoverageNodeRow[] = [];
    nearbyNodeRows: CoverageNodeRow[] = [];
    unlocatedNodeRows: CoverageNodeRow[] = [];
    coveredNodeRows: CoverageNodeRow[] = [];

    coverageSurface: CoverageSurface = 'map';
    showCoverageList = true;
    mapState: MapState = 'idle';
    hasMappableNodes = false;
    isMapUsable = false;

    startedAt: Date = new Date();
    maxStartedAt: Date = new Date();
    isEditingStartedAt = false;
    responseNote = '';

    isAcknowledged = false;
    acknowledgementLabel = '';
    acknowledgePending = false;
    responsePending = false;
    responseSubmitted = false;
    disableSubmitResponse = true;

    isConfirmingRevoke = false;
    revokePending = false;

    private token = '';
    private ackSource?: AlertEventTransitionSource.SmsDeepLink;
    private forceMapFailure = false;
    private nodes: PublicAlertResponseNode[] = [];
    private controls: PublicAlertResponseControlOption[] = [];
    private nodeSelectionById: Record<string, boolean> = {};

    private map: google.maps.Map | null = null;
    private advancedMarkerCtor: typeof google.maps.marker.AdvancedMarkerElement | null = null;
    private markersByNodeId = new Map<string, google.maps.marker.AdvancedMarkerElement>();
    private markerElementsByNodeId = new Map<string, HTMLElement>();
    private mapBounds: google.maps.LatLngBounds | null = null;
    private frameResizeObserver: ResizeObserver | null = null;
    private frameResizeTimer = 0;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private location: Location,
        private notificationService: NotificationService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private dateFormatService: DateFormatService,
        private cdr: ChangeDetectorRef,
    ) {
        this.timeFormat = this.dateFormatService.userTimeFormat;
        this.dateTimeFormat = this.dateFormatService.userDateWithTimeFormat;
    }

    ngOnInit(): void {
        this.token =
            this.route.snapshot.paramMap.get('token')?.trim() ||
            this.route.snapshot.queryParamMap.get('token')?.trim() ||
            '';
        if (this.route.snapshot.queryParamMap.get(ACK_LINK_CHANNEL_PARAM) === ACK_LINK_SMS_CHANNEL) {
            this.ackSource = AlertEventTransitionSource.SmsDeepLink;
        }
        // TEMPORARY local-preview flag for DASH-3076 (mockup states 3/4 - the
        // basemap failing to load). There is no real way to force a Google
        // Maps load failure on demand, so a query param stands in for it while
        // reviewing the design; delete alongside the mock-token scaffolding in
        // NotificationService once a real backend exists to test against.
        this.forceMapFailure = this.route.snapshot.queryParamMap.get('mapError') === '1';

        if (!this.token) {
            this.applyLinkRejected();
            this.loadErrorMessage = this.translateService.instant('This response link is invalid.');
            this.cdr.markForCheck();
            return;
        }

        this.loadPage();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.clearTimeout(this.frameResizeTimer);
        this.frameResizeObserver?.disconnect();
        this.frameResizeObserver = null;
        this.clearMarkers();
        this.map = null;
    }

    retryLoad(): void {
        if (!this.token || this.pageState === 'loading') {
            return;
        }

        this.pageState = 'loading';
        this.cdr.markForCheck();
        this.loadPage();
    }

    acknowledge(): void {
        if (!this.token || this.acknowledgePending || this.isAcknowledged) {
            return;
        }

        this.acknowledgePending = true;
        this.notificationService
            .acknowledgePublicAlertResponsePage(this.token, this.ackSource)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.acknowledgePending = false;
                    this.isAcknowledged = true;
                    this.transitionRows = this.buildTransitionRows(result.transitions);
                    this.acknowledgementLabel = this.buildAcknowledgementLabel(
                        result.acknowledgedAt,
                        result.acknowledgedByName,
                    );
                    this.startedAt = new Date();
                    this.maxStartedAt = new Date();
                    this.updateSubmitAvailability();
                    this.toastService.showSuccess('Alert acknowledged successfully', true);
                    this.cdr.markForCheck();
                    setTimeout(() => {
                        this.relocateMap();
                        this.applyMapInteraction();
                        this.refreshMarkerStates();
                    });
                },
                error: (error: unknown) => {
                    this.acknowledgePending = false;
                    this.toastService.showError(error, 'Failed to acknowledge this alert.');
                    this.cdr.markForCheck();
                },
            });
    }

    submitResponse(): void {
        if (!this.token || this.responsePending || this.disableSubmitResponse) {
            return;
        }

        const payload: CapturePublicAlertResponsePayload = {
            controlIds: this.getSelectedControlIds(),
            coveredNodeIds: this.getCoveredNodeIds(),
            startedAt: this.startedAt.toISOString(),
            responseNote: this.responseNote.trim() || undefined,
        };

        this.responsePending = true;
        this.notificationService
            .capturePublicAlertResponse(this.token, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.responsePending = false;
                    this.responseSubmitted = true;
                    this.updateSubmitAvailability();
                    this.toastService.showSuccess('Response recorded successfully', true);
                    this.cdr.markForCheck();
                },
                error: (error: unknown) => {
                    this.responsePending = false;
                    this.toastService.showError(error, 'Failed to record your response.');
                    this.cdr.markForCheck();
                },
            });
    }

    goToPreviousStep(): void {
        this.location.back();
    }

    startRevoke(): void {
        this.isConfirmingRevoke = true;
        this.cdr.markForCheck();
    }

    cancelRevoke(): void {
        this.isConfirmingRevoke = false;
        this.cdr.markForCheck();
    }

    /**
     * "Not you?" - records nothing and kills the token. The confirm step stands
     * between a mis-tap and a supervisor destroying the link they came to use.
     */
    confirmRevoke(): void {
        if (!this.token || this.revokePending) {
            return;
        }

        this.revokePending = true;
        this.notificationService
            .revokePublicAlertResponseLink(this.token)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.revokePending = false;
                    this.isConfirmingRevoke = false;
                    this.pageState = 'revoked';
                    this.clearMarkers();
                    this.map = null;
                    this.cdr.markForCheck();
                },
                error: (error: unknown) => {
                    this.revokePending = false;
                    this.toastService.showError(error, 'Could not revoke this link.');
                    this.cdr.markForCheck();
                },
            });
    }

    selectCoverageSurface(surface: CoverageSurface): void {
        if (surface === 'map' && !this.isMapUsable) {
            return;
        }

        this.coverageSurface = surface;
        this.showCoverageList = surface === 'list' || !this.isMapUsable;
        this.cdr.markForCheck();

        if (surface === 'map') {
            setTimeout(() => this.relocateMap());
        }
    }

    retryMap(): void {
        if (this.mapState === 'loading' || !this.hasMappableNodes) {
            return;
        }

        this.mapState = 'idle';
        this.map = null;
        this.clearMarkers();
        this.updateMapAvailability();
        this.cdr.markForCheck();
        setTimeout(() => {
            this.relocateMap();
            this.initializeMap();
        });
    }

    recentreMap(): void {
        const map = this.map;

        if (!map || !this.mapBounds || this.mapBounds.isEmpty()) {
            return;
        }

        map.setOptions({ minZoom: null, maxZoom: null });
        map.fitBounds(this.mapBounds, ALERT_RESPONSE_MAP_PADDING);

        if (typeof google === 'undefined' || !google.maps) {
            return;
        }

        google.maps.event.addListenerOnce(map, 'idle', () => {
            const fittedZoom = map.getZoom();

            if (fittedZoom != null) {
                map.setOptions({ minZoom: fittedZoom, maxZoom: fittedZoom });
            }
        });
    }

    toggleControl(controlId: string): void {
        if (this.responseSubmitted) {
            return;
        }

        this.selectedControlIds[controlId] = !this.selectedControlIds[controlId];
        this.selectedControlCount = this.getSelectedControlIds().length;
        this.updateSubmitAvailability();
    }

    onControlSearchChanged(searchText: string): void {
        this.controlSearchText = searchText;
        this.buildControlGroups();
    }

    toggleShowAllControls(): void {
        this.showAllControls = !this.showAllControls;
        this.buildControlGroups();
    }

    toggleNodeCoverage(nodeId: string): void {
        const node = this.nodes.find((item) => item.id === nodeId);

        if (!node || node.isCoverageLocked || this.responseSubmitted) {
            return;
        }

        this.nodeSelectionById[nodeId] = !this.nodeSelectionById[nodeId];
        this.refreshCoverage();
    }

    startEditingStartedAt(): void {
        this.maxStartedAt = new Date();
        this.isEditingStartedAt = true;
    }

    onStartedAtChanged(value: Date | null): void {
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            this.startedAt = value;
        }
    }

    confirmStartedAt(): void {
        this.isEditingStartedAt = false;
    }

    onResponseNoteChanged(note: string): void {
        this.responseNote = note;
    }

    private loadPage(): void {
        this.notificationService
            .getPublicAlertResponsePage(this.token)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    if (!data) {
                        this.applyLinkRejected();
                        this.cdr.markForCheck();
                        return;
                    }

                    this.applyPageData(data);
                    this.pageState = 'ready';
                    this.cdr.markForCheck();
                    setTimeout(() => {
                        this.relocateMap();
                        this.initializeMap();
                    });
                },
                error: (error: unknown) => {
                    this.applyLoadError(error);
                    this.cdr.markForCheck();
                },
            });
    }

    /**
     * A dropped connection is not an expired link. Saying so on a site with poor
     * connectivity sends a responder chasing a replacement SMS that was never
     * the problem, so an unreachable server is named as such and retried.
     */
    private applyLoadError(error: unknown): void {
        const status = error instanceof HttpErrorResponse ? error.status : null;
        const isLinkRejected =
            status === 400 || status === 401 || status === 403 || status === 404 || status === 410;

        if (isLinkRejected) {
            this.applyLinkRejected();
            return;
        }

        this.pageState = 'error';
        this.canRetryLoad = true;
        this.loadErrorTitle = this.translateService.instant('Could not reach the server.');
        this.loadErrorMessage = this.translateService.instant(
            'Your link is fine - the connection is not. Try again when you have signal.',
        );
    }

    private applyLinkRejected(): void {
        this.pageState = 'error';
        this.canRetryLoad = false;
        this.loadErrorTitle = this.translateService.instant('This link is no longer valid.');
        this.loadErrorMessage = this.translateService.instant(
            'It has expired or has already been used. Ask for a new one to be sent.',
        );
    }

    private applyPageData(data: PublicAlertResponsePageData): void {
        this.data = data;
        this.nodes = this.rankNodes(data.nodes ?? []);
        this.controls = this.rankControls(data.controls ?? []);
        this.isAcknowledged = data.isAcknowledged;

        this.headerTitle =
            data.ruleName || data.notificationName || this.translateService.instant('Alert');
        this.headerSeverityClass = this.getSeverityClass(data.severity);
        this.headerSeverityLabel = this.getSeverityLabel(data.severity);

        const alertingCount = this.nodes.filter((node) => node.isAlerting).length;
        this.breachSummaryLabel = this.translateService.instant(
            '{{count}} nodes breached in this evaluation pass and were sent as one message.',
            { count: alertingCount },
        );
        this.locateSummaryLabel = this.translateService.instant(
            '{{alerting}} alerting, {{nearby}} nearby',
            { alerting: alertingCount, nearby: this.nodes.length - alertingCount },
        );

        this.readingRows = this.buildReadingRows(data);
        // The page-data endpoint carries no acknowledged-by/acknowledged-at/transition fields
        // for a notification acknowledged before this page load - only this session's own
        // acknowledge() response below (or the local mock scaffolding) ever supplies them.
        // A reload of an already-acknowledged link with none of that still says so, just
        // without inventing a time or name the backend never sent.
        this.transitionRows = this.isAcknowledged ? this.buildTransitionRows(data.transitions ?? []) : [];
        if (this.isAcknowledged) {
            this.acknowledgementLabel = data.acknowledgedAt
                ? this.buildAcknowledgementLabel(data.acknowledgedAt, data.acknowledgedByName)
                : this.translateService.instant('This alert has already been acknowledged.');
        }

        this.selectedControlIds = {};
        this.controls
            .filter((control) => control.isSuggested)
            .forEach((control) => {
                this.selectedControlIds[control.id] = true;
            });

        this.nodeSelectionById = {};
        this.nodes.forEach((node) => {
            this.nodeSelectionById[node.id] = !!node.isCoverageLocked || node.isAlerting;
        });

        this.responseSubmitted = false;
        this.responseNote = '';
        if (data.submittedResponse) {
            this.applySubmittedResponse(data.submittedResponse);
        }

        this.selectedControlCount = this.getSelectedControlIds().length;
        this.controlCount = this.controls.length;

        this.hasMappableNodes = this.nodes.some((node) => this.hasCoordinates(node));
        this.updateMapAvailability();
        this.buildControlGroups();
        this.refreshCoverage();
    }

    /**
     * A response already recorded for this notification - through any token/recipient,
     * not necessarily this one - is shown as recorded rather than as an empty, re-editable
     * form a responder could resubmit differently.
     */
    private applySubmittedResponse(submittedResponse: NonNullable<PublicAlertResponsePageData['submittedResponse']>): void {
        this.responseSubmitted = true;
        this.responseNote = submittedResponse.responseNote ?? '';

        const startedAt = new Date(submittedResponse.startedAt);
        if (!Number.isNaN(startedAt.getTime())) {
            this.startedAt = startedAt;
        }

        submittedResponse.controlIds.forEach((controlId) => {
            this.selectedControlIds[controlId] = true;
        });
        submittedResponse.coveredNodeIds.forEach((nodeId) => {
            this.nodeSelectionById[nodeId] = true;
        });
    }

    private buildReadingRows(data: PublicAlertResponsePageData): ReadingRow[] {
        return (data.events ?? []).map((event) => ({
            id: event.id,
            nodeName: event.nodeName || this.translateService.instant('Unknown node'),
            severityClass: this.getSeverityClass(event.severity),
            severityLabel: this.getSeverityLabel(event.severity),
            readingValue: event.readingValue ?? null,
            unitLabel: event.unitLabel ?? '',
            thresholdValue: event.thresholdValue ?? null,
            detailLabel: [event.aggregationLabel, event.trendLabel]
                .filter((part) => !!part)
                .join(' · '),
            bandTextClass: `b${this.getBandLevel(event.bandLevel)}`,
            startedAt: event.startedAt ?? null,
            lastReportedAt: event.lastReportedAt ?? null,
            isReporting: !event.lastReportedAt,
        }));
    }

    private buildTransitionRows(
        transitions: PublicAlertEventTransitionResult[],
    ): TransitionRow[] {
        return transitions.map((transition, index) => ({
            id: transition.alertEventId || `transition-${index}`,
            nodeName: transition.nodeName || this.translateService.instant('Unknown node'),
            fromLabel: this.getStateLabel(transition.fromState),
            toLabel: this.getStateLabel(transition.toState),
        }));
    }

    private buildAcknowledgementLabel(
        acknowledgedAt?: Date | string | number | null,
        acknowledgedByName?: string,
    ): string {
        return this.translateService.instant('Acknowledged {{time}} by {{name}}.', {
            time: this.formatTime(acknowledgedAt ?? new Date()),
            name:
                acknowledgedByName ||
                this.data?.recipientName ||
                this.translateService.instant('you'),
        });
    }

    private getSeverityClass(severity: AlertSeverityLevel | string | null | undefined): string {
        return this.getSeverityLabel(severity).toLowerCase();
    }

    private getSeverityLabel(severity: AlertSeverityLevel | string | null | undefined): string {
        if (typeof severity === 'number') {
            return alertSeverityLabel(severity);
        }

        const named = String(severity ?? '').trim();
        return named || alertSeverityLabel(AlertSeverityLevel.Watch);
    }

    private getStateLabel(state: AlertEventState | string): string {
        if (typeof state === 'string' && state.trim()) {
            return state;
        }

        switch (state) {
            case AlertEventState.Acknowledged:
                return this.translateService.instant('Acknowledged');
            case AlertEventState.Resolved:
                return this.translateService.instant('Resolved');
            case AlertEventState.Escalated:
                return this.translateService.instant('Escalated');
            default:
                return this.translateService.instant('Active');
        }
    }

    private rankNodes(nodes: PublicAlertResponseNode[]): PublicAlertResponseNode[] {
        return [...nodes].sort((left, right) => {
            if (left.isAlerting !== right.isAlerting) {
                return left.isAlerting ? -1 : 1;
            }

            const leftDistance = left.distanceMeters ?? Number.POSITIVE_INFINITY;
            const rightDistance = right.distanceMeters ?? Number.POSITIVE_INFINITY;

            if (leftDistance !== rightDistance) {
                return leftDistance - rightDistance;
            }

            return left.name.localeCompare(right.name);
        });
    }

    private rankControls(
        controls: PublicAlertResponseControlOption[],
    ): PublicAlertResponseControlOption[] {
        return [...controls].sort((left, right) => {
            if (left.isSuggested !== right.isSuggested) {
                return left.isSuggested ? -1 : 1;
            }

            if (left.isRecentlyUsed !== right.isRecentlyUsed) {
                return left.isRecentlyUsed ? -1 : 1;
            }

            return left.name.localeCompare(right.name);
        });
    }

    private buildControlGroups(): void {
        const searchText = this.controlSearchText.trim().toLowerCase();
        const matching = searchText
            ? this.controls.filter(
                  (control) =>
                      control.name.toLowerCase().includes(searchText) ||
                      (control.categoryName ?? '').toLowerCase().includes(searchText),
              )
            : this.controls;

        this.hasControlMatches = matching.length > 0;

        const suggested = matching.filter((control) => control.isSuggested);
        const recent = matching.filter(
            (control) => !control.isSuggested && control.isRecentlyUsed,
        );
        const remaining = matching.filter(
            (control) => !control.isSuggested && !control.isRecentlyUsed,
        );

        const isCollapsed = !searchText && !this.showAllControls;
        const visibleRemaining = isCollapsed
            ? remaining.slice(0, ALERT_RESPONSE_COLLAPSED_CONTROL_COUNT)
            : remaining;
        this.hiddenControlCount = remaining.length - visibleRemaining.length;

        this.controlGroups = [
            {
                id: 'suggested',
                title: this.translateService.instant('Suggested by this rule'),
                hint: '',
                options: suggested,
            },
            {
                id: 'recent',
                title: this.translateService.instant('Recently used here'),
                hint: this.translateService.instant('at this site, this week'),
                options: recent,
            },
            {
                id: 'all',
                title: this.translateService.instant('All controls'),
                hint: this.translateService.instant('{{count}} at this site', {
                    count: this.data?.totalControlCount ?? this.controls.length,
                }),
                options: visibleRemaining,
            },
        ].filter((group) => group.options.length > 0);

        this.cdr.markForCheck();
    }

    private refreshCoverage(): void {
        this.lockedNodeRows = this.nodes
            .filter((node) => node.isCoverageLocked)
            .map((node) => this.toCoverageRow(node));
        this.nearbyNodeRows = this.nodes
            .filter((node) => !node.isCoverageLocked && this.hasCoordinates(node))
            .map((node) => this.toCoverageRow(node));
        this.unlocatedNodeRows = this.nodes
            .filter((node) => !node.isCoverageLocked && !this.hasCoordinates(node))
            .map((node) => this.toCoverageRow(node));
        this.coveredNodeRows = this.nodes
            .filter((node) => this.nodeSelectionById[node.id])
            .map((node) => this.toCoverageRow(node));

        this.refreshMarkerStates();
        this.updateSubmitAvailability();
    }

    private toCoverageRow(node: PublicAlertResponseNode): CoverageNodeRow {
        return {
            id: node.id,
            name: node.name,
            distanceLabel: this.getDistanceLabel(node),
            isCovered: !!this.nodeSelectionById[node.id],
            isLocked: !!node.isCoverageLocked,
        };
    }

    private getDistanceLabel(node: PublicAlertResponseNode): string {
        if (node.distanceMeters == null || !Number.isFinite(node.distanceMeters)) {
            return this.translateService.instant('distance unknown');
        }

        if (node.distanceMeters >= 1000) {
            return `${(node.distanceMeters / 1000).toFixed(1)} km`;
        }

        return `${Math.round(node.distanceMeters)} m`;
    }

    private updateMapAvailability(): void {
        this.isMapUsable = this.hasMappableNodes && this.mapState !== 'failed';

        if (!this.isMapUsable) {
            this.coverageSurface = 'list';
        }

        this.showCoverageList = this.coverageSurface === 'list' || !this.isMapUsable;
    }

    private updateSubmitAvailability(): void {
        this.disableSubmitResponse =
            !this.isAcknowledged ||
            !this.data?.canCaptureResponse ||
            this.responseSubmitted ||
            !this.getSelectedControlIds().length ||
            !this.getCoveredNodeIds().length;

        this.cdr.markForCheck();
    }

    private getSelectedControlIds(): string[] {
        return this.controls
            .filter((control) => this.selectedControlIds[control.id])
            .map((control) => control.id);
    }

    private getCoveredNodeIds(): string[] {
        return this.nodes
            .filter((node) => this.nodeSelectionById[node.id])
            .map((node) => node.id);
    }

    private hasCoordinates(node: PublicAlertResponseNode): boolean {
        return (
            typeof node.latitude === 'number' &&
            Number.isFinite(node.latitude) &&
            typeof node.longitude === 'number' &&
            Number.isFinite(node.longitude)
        );
    }

    private formatTime(value: Date | string | number): string {
        const parsed = value instanceof Date ? value : new Date(value);

        if (Number.isNaN(parsed.getTime())) {
            return '';
        }

        return this.dateFormatService.formatDateTimeToString(parsed, this.timeFormat);
    }

    private getBandLevel(bandLevel?: number | null): number {
        return Math.min(Math.max(Math.round(bandLevel ?? 1), 1), 5);
    }

    private initializeMap(): void {
        if (!this.hasMappableNodes || this.mapState === 'loading' || this.map) {
            return;
        }

        if (this.forceMapFailure) {
            this.failMap();
            return;
        }

        this.mapState = 'loading';
        this.cdr.markForCheck();

        getGoogleMapsLoader(this.translateService.currentLang)
            .load()
            .then(async () => {
                const mapElement =
                    this.mapFrameRef?.nativeElement.querySelector<HTMLElement>('.map-canvas');

                if (!mapElement) {
                    this.failMap();
                    return;
                }

                const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
                const { AdvancedMarkerElement } = (await google.maps.importLibrary(
                    'marker',
                )) as google.maps.MarkerLibrary;
                this.advancedMarkerCtor = AdvancedMarkerElement;

                const mappableNodes = this.nodes.filter((node) => this.hasCoordinates(node));

                this.map = new Map(mapElement, {
                    center: {
                        lat: mappableNodes[0].latitude as number,
                        lng: mappableNodes[0].longitude as number,
                    },
                    zoom: ALERT_RESPONSE_MAP_ZOOM,
                    mapId: ALERT_RESPONSE_MAP_ID,
                    mapTypeId: 'roadmap',
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    ...this.getMapInteractionOptions(),
                });

                this.renderMarkers();
                this.recentreMap();
                this.observeFrameResize();
                this.mapState = 'ready';
                this.updateMapAvailability();
                this.cdr.markForCheck();
            })
            .catch(() => this.failMap());
    }

    /**
     * A gesture that changes the view can be made by accident while selecting,
     * and there is nothing outside the frame worth navigating to. The coverage
     * map pans - a stray drag is recoverable with Recentre - but never zooms.
     * Before the confirm it does not move at all.
     *
     * 'greedy' is deliberately avoided here: per the Maps JS API docs it
     * "essentially disables one-finger touch scrolling of the containing web
     * page", so a swipe that starts over the map never reaches the page - on
     * a phone-sized map frame that is most of the scrollable area. 'cooperative'
     * keeps a single tap selecting a marker (a tap is not a pan gesture) while
     * a one-finger swipe scrolls the page and only a two-finger drag pans the map.
     */
    private getMapInteractionOptions(): google.maps.MapOptions {
        return {
            gestureHandling: this.isAcknowledged ? 'cooperative' : 'none',
            zoomControl: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
        };
    }

    private applyMapInteraction(): void {
        this.map?.setOptions(this.getMapInteractionOptions());
    }

    private failMap(): void {
        this.mapState = 'failed';
        this.map = null;
        this.clearMarkers();
        this.updateMapAvailability();
        this.cdr.markForCheck();
    }

    /**
     * Re-parenting the map container detaches every AdvancedMarkerElement's
     * content, so the markers are rebuilt whenever the frame moves between the
     * locate step and the coverage step.
     */
    private renderMarkers(): void {
        const AdvancedMarkerElement = this.advancedMarkerCtor;

        if (!this.map || !AdvancedMarkerElement) {
            return;
        }

        this.clearMarkers();

        const mappableNodes = this.nodes.filter((node) => this.hasCoordinates(node));
        const focusedNodeIds = new Set(this.getFocusedNodeIds(mappableNodes));
        const bounds = new google.maps.LatLngBounds();

        mappableNodes.forEach((node) => {
            const content = this.buildMarkerElement(node);
            const marker = new AdvancedMarkerElement({
                map: this.map,
                position: {
                    lat: node.latitude as number,
                    lng: node.longitude as number,
                },
                title: node.name,
                content,
            });

            marker.addListener('click', () => {
                if (!this.isAcknowledged) {
                    return;
                }

                this.toggleNodeCoverage(node.id);
                this.cdr.markForCheck();
            });

            this.markersByNodeId.set(node.id, marker);
            this.markerElementsByNodeId.set(node.id, content);

            if (focusedNodeIds.has(node.id)) {
                bounds.extend({
                    lat: node.latitude as number,
                    lng: node.longitude as number,
                });
            }
        });

        this.mapBounds = bounds;
        this.refreshMarkerStates();
    }

    /**
     * The map frame is one height on a phone and taller from tablet width up, so
     * a breakpoint crossing or a rotation changes the viewport the bounds were
     * fitted to. Refit rather than leave the alerting nodes off-frame.
     */
    private observeFrameResize(): void {
        const frame = this.mapFrameRef?.nativeElement;

        if (!frame || this.frameResizeObserver) {
            return;
        }

        this.frameResizeObserver = new ResizeObserver(() => {
            window.clearTimeout(this.frameResizeTimer);
            this.frameResizeTimer = window.setTimeout(
                () => this.recentreMap(),
                ALERT_RESPONSE_MAP_REFIT_DELAY_MS,
            );
        });
        this.frameResizeObserver.observe(frame);
    }

    private relocateMap(): void {
        const frame = this.mapFrameRef?.nativeElement;
        const anchor = this.isAcknowledged
            ? this.coverageAnchorRef?.nativeElement
            : this.locateAnchorRef?.nativeElement;

        if (!frame || !anchor || frame.parentElement === anchor) {
            return;
        }

        anchor.appendChild(frame);
        this.renderMarkers();
        this.recentreMap();
    }

    private clearMarkers(): void {
        const hasMapsApi = typeof google !== 'undefined' && !!google.maps;

        this.markersByNodeId.forEach((marker) => {
            if (hasMapsApi) {
                google.maps.event.clearInstanceListeners(marker);
            }

            marker.map = null;
        });
        this.markersByNodeId.clear();
        this.markerElementsByNodeId.clear();
        this.mapBounds = null;
    }

    private refreshMarkerStates(): void {
        this.markerElementsByNodeId.forEach((element, nodeId) => {
            element.classList.toggle(
                'on',
                this.isAcknowledged && !!this.nodeSelectionById[nodeId],
            );
            element.classList.toggle('readonly', !this.isAcknowledged);
        });
    }

    /**
     * Band colour is the fill, alert state is a structural border, and coverage
     * is the label pill - never a third ring, which would collide with the
     * critical halo.
     */
    private buildMarkerElement(node: PublicAlertResponseNode): HTMLElement {
        const marker = document.createElement('div');
        marker.className = 'amk';

        if (node.isAlerting) {
            marker.classList.add('alerting');
            marker.classList.add(`sev-${this.getSeverityClass(node.severity)}`);
        }

        if (!node.isReporting) {
            marker.classList.add('offline');
        }

        const dot = document.createElement('span');
        dot.className = `amk-dot f${this.getBandLevel(node.bandLevel)}`;

        if (!node.isReporting) {
            const noData = document.createElement('span');
            noData.className = 'amk-nd';
            noData.textContent = '?';
            dot.appendChild(noData);
        }

        const check = document.createElement('span');
        check.className = 'amk-check';
        check.textContent = '✓';
        dot.appendChild(check);

        const label = document.createElement('span');
        label.className = 'amk-lbl';
        label.textContent = node.name;

        if (!node.isReporting && node.lastReportedAt) {
            const staleness = document.createElement('span');
            staleness.className = 'm';
            staleness.textContent = this.translateService.instant('no data since {{time}}', {
                time: this.formatTime(node.lastReportedAt),
            });
            label.appendChild(staleness);
        }

        marker.appendChild(dot);
        marker.appendChild(label);

        return marker;
    }

    private getFocusedNodeIds(nodes: PublicAlertResponseNode[]): string[] {
        const alertingNodes = nodes.filter((node) => node.isAlerting);

        if (!alertingNodes.length) {
            return nodes.map((node) => node.id);
        }

        const neighbourIds = nodes
            .filter((node) => !node.isAlerting)
            .map((node) => ({
                id: node.id,
                distanceMeters: this.getMinimumDistanceToAlertingNode(node, alertingNodes),
            }))
            .sort((left, right) => left.distanceMeters - right.distanceMeters)
            .slice(0, ALERT_RESPONSE_MAX_NEIGHBOUR_COUNT)
            .map((item) => item.id);

        return [...new Set([...alertingNodes.map((node) => node.id), ...neighbourIds])];
    }

    private getMinimumDistanceToAlertingNode(
        node: PublicAlertResponseNode,
        alertingNodes: PublicAlertResponseNode[],
    ): number {
        let minDistanceMeters = Number.POSITIVE_INFINITY;

        alertingNodes.forEach((alertingNode) => {
            const distanceMeters = this.computeDistanceMeters(
                node.latitude as number,
                node.longitude as number,
                alertingNode.latitude as number,
                alertingNode.longitude as number,
            );

            if (distanceMeters < minDistanceMeters) {
                minDistanceMeters = distanceMeters;
            }
        });

        return minDistanceMeters;
    }

    private computeDistanceMeters(
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number,
    ): number {
        const latDelta = this.toRadians(lat2 - lat1);
        const lonDelta = this.toRadians(lon2 - lon1);
        const startLat = this.toRadians(lat1);
        const endLat = this.toRadians(lat2);

        const haversine =
            Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
            Math.cos(startLat) *
                Math.cos(endLat) *
                Math.sin(lonDelta / 2) *
                Math.sin(lonDelta / 2);

        return (
            EARTH_RADIUS_METERS *
            2 *
            Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
        );
    }

    private toRadians(value: number): number {
        return value * (Math.PI / 180);
    }
}
